import React, { useState } from "react";
import {
  MynauiEditOne,
  TablerCaretUpDownFilled,
} from "../../assets/icons/icons";
import useSortableData from "../../utils/SortData";
import { Trash2 } from "lucide-react";
import EditCustomerDetails from "./EditCustomerDetails";
import { deleteCustomer } from "../../actions/CustomerAction";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ConfirmDialogBoxWithUser from "../layout/ConfirmDialogBoxWithUser";
import EmptyTableRecord from "../layout/EmptyTableRecord";
import { formatDateParts } from "../../utils/DateFormat";
import SearchBar from "../layout/SearchBar";
import { useSession } from "../layout/SessionContext";

const TABLE_HEADERS = [
  { key: "uid", label: "Number" },
  { key: "createdAt", label: "Date Joined" },
  { key: "mobileNumber", label: "Mobile Number" },
  { key: "email", label: "Email Address" },
  { key: "referral.code", label: "Referral Code " },
  { key: "status", label: "Status", isStatus: true },
  { key: "productCount", label: "Action", isAction: true },
];

// Status style mapping
export const getStatusStyle = (status) => {
  switch (status?.toUpperCase()) {
    case "ACTIVE":
      return "bg-[#D1FAE5] text-[#065F46]";
    case "DELETED":
      return "bg-[#FEE2E2] text-[#991B1B]";
    case "SUSPENDED":
      return "bg-[#F3F4F6] text-[#1F2937]";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const CustomerTable = ({
  customers,
  refreshData,
  searchTerm,
  setSearchTerm,
  totalCustomers,
  referralCode,
}) => {
  const { hasPermission } = useSession();
  const [confirmEditDialog, setConfirmEditDialog] = useState(false);
  const [confirmDeleteDialogBox, setConfirmDeleteDialogBox] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [userName, setUserName] = useState("");
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm || "");

  const { sortedItems: sortedCustomers, handleSort } =
    useSortableData(customers);

  const handleSearch = () => {
    setSearchTerm(localSearchTerm);
  };

  const handleEditCustomer = (customerId) => {
    const customerToEdit = customers.find(
      (customer) => customer._id === customerId
    );
    setSelectedCustomer(customerToEdit);
    setConfirmEditDialog(true);
  };
  const handleConfirmDeleteDialogBox = (customerId) => {
    const customerToDelete = customers.find(
      (customer) => customer._id === customerId
    );
    setSelectedCustomer(customerToDelete);
  };
  const handleDeleteCustomer = async (customerId, name) => {
    if (!name) {
      toast.error("Enter your username.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("customerId", customerId);
      formData.append("username", name);

      const response = await deleteCustomer(formData.get("customerId"));

      if (response?.success) {
        toast.success(response.message || "Customer deleted successfully.");
        setUserName("");
        setConfirmDeleteDialogBox(false);
        refreshData();
      } else {
        toast.error(response?.message || "Failed to delete customer.");
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  return (
    <div>
      <div className="flex justify-between items-center w-full ">
        {/* Statistics Cards */}
        <div className="border border-gray-200 rounded-lg relative overflow-hidden">
          <div className="flex p-2">
            <div className="px-2 text-center gap-2">
              <div className="text-gray-500 text-xs uppercase font-medium w-fit mb-2">
                REFERRAL CODE
              </div>
              <div className="text-lg font-bold text-text1">
                {referralCode || "-"}
              </div>
            </div>
            <div className="px-2 text-center border-l border-gray-200">
              <div className="text-gray-500 text-xs uppercase font-medium mb-2">
                TOTAL NO.OF CUSTOMERS JOINED
              </div>
              <div className="text-lg font-bold text-text1">
                {totalCustomers}
              </div>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <SearchBar
          searchTerm={localSearchTerm}
          onChange={(value) => setLocalSearchTerm(value)}
          onSearch={handleSearch}
          className="w-96"
        />
      </div>

      <div className=" rounded-lg border-t border-x border-b border-gray-300 overflow-x-auto mt-6">
        <table className="w-full  border-gray-300 rounded-md">
          <thead className="bg-gray-100 border-b ">
            <tr className="md:h-10">
              {TABLE_HEADERS.map((header, index) => {
                if (header.isAction && !hasPermission("Customers", "edit")) {
                  return null;
                }
                return (
                  <th
                    key={index}
                    className="py-3 text-center font-semibold text-[15px] md:h-10 text-text1 whitespace-nowrap px-2"
                  >
                    {!header.isAction ? (
                      <div
                        className="flex items-center cursor-pointer text-left"
                        onClick={() => handleSort(header.key)}
                      >
                        <TablerCaretUpDownFilled className="mr-2" />
                        {header.label}
                      </div>
                    ) : (
                      <div className="flex text-left items-center">
                        {header.label}
                      </div>
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {sortedCustomers.length > 0 ? (
              sortedCustomers.map((customer) => (
                <tr key={customer._id} className="hover:bg-gray-50 md:h-[60px]">
                  {TABLE_HEADERS.map((header) => {
                    if (header.isAction && !hasPermission("Customers", "edit")) {
                      return null;
                    }
                    return (
                      <td
                        key={header.key}
                        className=" pl-2  text-[#374151] text-left font-rubik text-[15px] md:h-[60px]"
                      >
                        {header.key === "createdAt" ? (
                          <span className="whitespace-nowrap">
                          {(() => {
                              const { date, time } = formatDateParts(customer[header.key]);
                              return (
                                <>
                                  <div>{date}</div>
                                  <div>{time}</div>
                                </>
                              );
                            })()}
                          </span>
                        ) : header.isAction ? (
                          <div className="flex justify-start min-h-6">
                            {customer.status !== "DELETED" && (
                              <>
                                <div className="flex justify-center">
                                  <button
                                    onClick={() =>
                                      handleEditCustomer(customer._id)
                                    }
                                    className="p-2 rounded bg-gray-100 sm:bg-transparent"
                                  >
                                    <MynauiEditOne className="text-gray-400" />
                                  </button>
                                </div>
                                <div className="flex justify-center">
                                  <button
                                    onClick={() => {
                                      handleConfirmDeleteDialogBox(
                                        customer._id
                                      );
                                      setConfirmDeleteDialogBox(true);
                                    }}
                                    className="p-2 rounded bg-gray-100 sm:bg-transparent"
                                  >
                                    <Trash2 className="text-gray-400 h-5" />
                                  </button>
                                </div>
                              </>
                            )}
                          </div>
                        ) : header.isStatus ? (
                          <span
                            className={`px-3 py-1 rounded-full font-medium text-[12px]  ${getStatusStyle(
                              customer.status
                            )}`}
                          >
                            {customer.status || ""}
                          </span>
                        ) : header.key === "referral.code" ? (
                          customer.referral?.code ? (
                            customer.referral.code
                          ) : (
                            "N/A"
                          )
                        ) : header.key === "email" ? (
                          customer.email ? (
                            customer.email
                          ) : (
                            "N/A"
                          )
                        ) : (
                          customer[header.key] || "N/A"
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={TABLE_HEADERS.length}
                  className=" text-center text-gray-500"
                >
                  <EmptyTableRecord />
                </td>
              </tr>
            )}
          </tbody>
        </table>

        {confirmDeleteDialogBox && (
          <ConfirmDialogBoxWithUser
            handleCancel={() => setConfirmDeleteDialogBox(false)}
            message="You’re about to <strong> delete the customer data. </strong>Please confirm by entering your username."
            type="inactive"
            handleConfirm={() =>
              handleDeleteCustomer(selectedCustomer._id, userName)
            }
            userName={userName}
            setuserName={setUserName}
            confirmMessage="Confirm & Delete"
          />
        )}
        {confirmEditDialog && (
          <EditCustomerDetails
            closeModal={() => setConfirmEditDialog(false)}
            customer={selectedCustomer}
            refreshData={refreshData}
          />
        )}
      </div>
    </div>
  );
};

export default CustomerTable;
